// v4
// Path: frontend/src/components/SyncHub/shared/PresetAccordionItem.jsx
// Inputs: preset, isExpanded, onToggleExpand, token, sourceSystem, targetSystem, availableTables, targetEntities, assignedEntitiesInSet, onSavePreset, onDuplicatePreset, onRenamePreset, onDeletePreset, onResetPresetDefaults, onSavingStateChange
// Outputs: Tier 2 4-column Tree Grid Pipeline row (250px minmax(150px, 1fr) 80px minmax(150px, 1fr)) with Action HUD, stacking sticky header, CSS grid slide animation, auto-scroll, dynamic table filtering, auto-spawning required fields, and optimistic debounced auto-saving.
// Summary: High-performance pipeline accordion item managing self-contained ETL field mappings, persistent ghost rows, locked required destination fields, opaque sticky header, decoupled expand chevron, double-click name editing, field search HUD, and safe delete UX.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SearchableSelect from '../SearchableSelect';
import MappingDesignerTable from './MappingDesignerTable';
import { getQbEntityMeta, normalizeEntityName } from '../entityCatalogs';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

// Helper to merge target fields with existing preset mappings
// Condensed rule: DO NOT pre-render all unmapped fields from target schema!
// ONLY retain: 1. Auto-spawned required fields, 2. Actively mapped rows, 3. User-created placeholder rows.
const mergeTargetFieldsWithMappingsCondensed = (tFields, rawMappings) => {
    const existingMap = new Map();
    (rawMappings || []).forEach(m => {
        if (m && m.target) {
            existingMap.set(m.target.toLowerCase(), {
                target: m.target,
                label: m.label || m.target,
                sources: Array.isArray(m.sources) && m.sources.length > 0
                    ? m.sources
                    : (m.source ? (m.source.includes('+') ? m.source.split('+').map(s => s.trim()) : [m.source]) : []),
                source: m.source || (Array.isArray(m.sources) ? m.sources.join(m.delimiter !== undefined ? m.delimiter : ' - ') : ''),
                delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                template: m.template || '',
                isCustom: !!m.isCustom
            });
        }
    });

    // Auto-spawn required destination fields from target schema if missing
    (tFields || []).forEach(tf => {
        const isRequired = !!tf.required || (typeof tf.rules === 'string' && tf.rules.toUpperCase().includes('REQUIRED'));
        const lower = tf.name.toLowerCase();
        if (isRequired && !existingMap.has(lower)) {
            existingMap.set(lower, {
                target: tf.name,
                label: tf.label || tf.name,
                sources: [],
                source: '',
                delimiter: ' - ',
                template: '',
                isRequired: true
            });
        }
    });

    return Array.from(existingMap.values());
};

export default function PresetAccordionItem({
    preset,
    isExpanded = false,
    onToggleExpand = () => {},
    token,
    sourceSystem = 'Passport',
    targetSystem = 'QuickBooks',
    availableTables = [],
    targetEntities = [],
    assignedEntitiesInSet = new Set(),
    onSavePreset = async () => {},
    onDuplicatePreset = () => {},
    onRenamePreset = () => {},
    onDeletePreset = () => {},
    onResetPresetDefaults = () => {},
    onSavingStateChange = () => {}
}) {
    const containerRef = useRef(null);

    // Current entity and table for this preset pipeline
    const canonicalEntity = normalizeEntityName(preset.TargetQBEntity || preset.EntityType || 'Customer');
    const entityMeta = getQbEntityMeta(canonicalEntity);
    const defaultTable = preset.SourceTable || entityMeta?.defaultTable || '';

    const [selectedTable, setSelectedTable] = useState(defaultTable);
    const [selectedEntity, setSelectedEntity] = useState(canonicalEntity);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [loadingDefinitions, setLoadingDefinitions] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    // Inline pipeline name editing state (Double-click feature)
    const [isEditingName, setIsEditingName] = useState(false);
    const [pipelineName, setPipelineName] = useState(preset.MappingName || `${canonicalEntity} Pipeline`);

    // Safe delete UX tracking
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(false);

    // Tier 2 HUD field search & filter toggles
    const [fieldSearch, setFieldSearch] = useState('');
    const [mappingFilter, setMappingFilter] = useState('all'); // 'all' | 'mapped' | 'unmapped'

    // Debounced Auto-Save Refs
    const saveTimerRef = useRef(null);
    const latestStateRef = useRef({ mappings, selectedTable, selectedEntity });

    useEffect(() => {
        latestStateRef.current = { mappings, selectedTable, selectedEntity };
    }, [mappings, selectedTable, selectedEntity]);

    // Sync state if preset updates from parent
    useEffect(() => {
        setSelectedTable(preset.SourceTable || entityMeta?.defaultTable || '');
        setSelectedEntity(canonicalEntity);
        setPipelineName(preset.MappingName || `${canonicalEntity} Pipeline`);
    }, [preset.SourceTable, preset.MappingName, canonicalEntity, entityMeta?.defaultTable]);

    // Auto-Scroll to Top on Expand (delayed 100ms to allow layout calculation & smooth transition)
    useEffect(() => {
        if (isExpanded && containerRef.current) {
            const timer = setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isExpanded]);

    // Fetch field definitions when expanded
    const fetchDefinitions = useCallback(async () => {
        if (!isExpanded) return;
        setLoadingDefinitions(true);
        try {
            const activeTbl = selectedTable || preset.SourceTable || '';
            const params = new URLSearchParams({
                entityType: selectedEntity,
                sourceDatabaseName: 'pbsdata00',
                tableName: activeTbl
            });

            const res = await fetch(`/api/sync/field-definitions/${encodeURIComponent(selectedEntity)}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await unbox(res);
                setDefinitions(data);

                let initialRaw = Array.isArray(preset.mappings) ? preset.mappings : [];
                if ((!initialRaw || initialRaw.length === 0) && preset.FieldMappingsJson) {
                    try { initialRaw = JSON.parse(preset.FieldMappingsJson); } catch(e) {}
                }

                const targetFields = data.targetFields || [];
                const merged = mergeTargetFieldsWithMappingsCondensed(targetFields, initialRaw);
                setMappings(merged);
                setIsDirty(false);
            }
        } catch (err) {
            console.error('[PresetAccordionItem] Error loading definitions:', err);
        } finally {
            setLoadingDefinitions(false);
        }
    }, [isExpanded, selectedTable, selectedEntity, preset.SourceTable, preset.mappings, preset.FieldMappingsJson, token]);

    useEffect(() => {
        if (isExpanded) {
            fetchDefinitions();
        }
    }, [isExpanded, fetchDefinitions]);

    // Optimistic Save Routine
    const executeSave = useCallback(async (stateToSave) => {
        const { mappings: curMappings, selectedTable: curTable, selectedEntity: curEntity } = stateToSave;
        setSaving(true);
        onSavingStateChange(true);
        try {
            const cleanMappings = curMappings
                .filter(m => m.target)
                .map(m => ({
                    target: m.target.trim(),
                    label: m.label ? m.label.trim() : undefined,
                    source: m.source ? m.source.trim() : (m.sources ? m.sources.join(m.delimiter || ' - ') : ''),
                    sources: Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : (m.source ? [m.source.trim()] : []),
                    delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                    template: m.template || undefined
                }));

            await onSavePreset({
                mappingId: preset.MappingID,
                setName: preset.SetName,
                sourceSystem: preset.SourceSystem || sourceSystem,
                targetSystem: preset.TargetSystem || targetSystem,
                entityType: curEntity,
                targetQBEntity: curEntity,
                sourceTable: curTable,
                mappingName: preset.MappingName,
                mappings: cleanMappings
            });

            setIsDirty(false);
        } catch (err) {
            console.error('[PresetAccordionItem] Auto-save error:', err);
        } finally {
            setSaving(false);
            onSavingStateChange(false);
        }
    }, [preset, sourceSystem, targetSystem, onSavePreset, onSavingStateChange]);

    // Trigger debounced save
    const triggerDebouncedSave = useCallback(() => {
        setIsDirty(true);
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            executeSave(latestStateRef.current);
        }, 1500);
    }, [executeSave]);

    // Flush pending save immediately on collapse
    useEffect(() => {
        if (!isExpanded && isDirty) {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
            executeSave(latestStateRef.current);
        }
    }, [isExpanded, isDirty, executeSave]);

    // Compute live mapping coverage count
    const mappedFieldsCount = useMemo(() => {
        if (mappings.length > 0) {
            return mappings.filter(m => {
                const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                return s.length > 0 && s.some(x => x && x.trim().length > 0);
            }).length;
        }
        const raw = Array.isArray(preset.mappings) ? preset.mappings : [];
        return raw.filter(m => {
            const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
            return s.length > 0 && s.some(x => x && x.trim().length > 0);
        }).length;
    }, [mappings, preset.mappings]);

    // Mutate field mapping row
    const handleCustomMappingChange = (targetName, field, val) => {
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const updated = { ...m, [field]: val };
                if (field === 'sources') {
                    const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                    updated.source = Array.isArray(val) ? val.join(delim) : String(val);
                }
                return updated;
            }
            return m;
        }));
        triggerDebouncedSave();
    };

    // Remove mapping row (immediate save so caller can await)
    const handleRemoveCustomRow = async (targetName) => {
        const nextMappings = mappings.filter(m => (m.target || '').toLowerCase() !== targetName.toLowerCase());
        setMappings(nextMappings);
        await executeSave({ mappings: nextMappings, selectedTable, selectedEntity });
    };

    // Reset mapping row (clear assigned sources)
    const handleResetMappingRow = (targetName) => {
        setMappings(prev => prev.map(m => {
            if ((m.target || '').toLowerCase() === targetName.toLowerCase()) {
                return { ...m, sources: [], source: '' };
            }
            return m;
        }));
        triggerDebouncedSave();
    };

    // Add new mapping from Tier 3 Ghost Row
    const handleAddMappingFromGhost = (newMapping) => {
        if (!newMapping.target) return;
        setMappings(prev => {
            const exists = prev.some(m => (m.target || '').toLowerCase() === newMapping.target.toLowerCase());
            if (exists) return prev;
            return [...prev, newMapping];
        });
        triggerDebouncedSave();
    };

    // Prepare table dropdown options
    const tableOptions = useMemo(() => {
        return availableTables.map(t => {
            const tName = typeof t === 'string' ? t : (t.name || t.TableName || '');
            const fCount = t.fieldCount || t.FieldCount || (t.columns ? t.columns.length : 0);
            const countStr = fCount > 0 ? ` (${fCount} cols)` : '';
            return {
                value: tName,
                label: `${tName}${countStr}`,
                description: t.description || t.Description || undefined,
                isPowered: tName.toUpperCase() === (selectedTable || '').toUpperCase() && mappedFieldsCount > 0
            };
        });
    }, [availableTables, selectedTable, mappedFieldsCount]);

    // Prepare entity dropdown options strictly excluding other pipelines' entities in this Set
    const entityOptions = useMemo(() => {
        const curLower = selectedEntity.toLowerCase();
        return targetEntities
            .filter(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                return eName.toLowerCase() === curLower || !assignedEntitiesInSet.has(eName.toLowerCase());
            })
            .map(ent => {
                const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                const eLabel = typeof ent === 'string' ? ent : (ent.label || ent.name || ent.entity);
                const isCurrent = eName.toLowerCase() === curLower;
                return {
                    value: eName,
                    label: eLabel,
                    isPowered: isCurrent ? mappedFieldsCount > 0 : false
                };
            });
    }, [targetEntities, selectedEntity, assignedEntitiesInSet, mappedFieldsCount]);

    return (
        <div
            ref={containerRef}
            className={`transition-colors duration-200 border-b border-slate-700/50 ${
                isExpanded
                    ? 'bg-slate-800/40'
                    : 'bg-slate-900/60 hover:bg-slate-850/60'
            }`}
        >
{/* ══════════════════════════════════════════════════════════════════ */}
            {/* TIER 2: 4-COLUMN TREE GRID PIPELINE HEADER ROW                     */}
            {/* Ultra-condensed h-9, px-2 py-0.5, sticky top-16 (64px)             */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div
                className={`grid grid-cols-[250px_minmax(150px,1fr)_80px_minmax(150px,1fr)] gap-2 items-center h-9 max-h-9 px-2 py-0.5 select-none transition-colors border-b relative group ${
                    deleteError ? 'border-rose-500 bg-rose-950/20' : 'border-slate-700/50'
                } ${
                    isDeleting
                        ? 'opacity-50 text-slate-500 bg-slate-900'
                        : isExpanded
                            ? 'sticky top-16 z-40 bg-slate-900 shadow-sm'
                            : 'bg-slate-850/80 hover:bg-slate-800'
                }`}
            >
                {/* ── COL 1 (250px): EXPAND CHEVRON BUTTON + PIPELINE TITLE (DOUBLE CLICK TO EDIT) ── */}
                <div className="flex items-center gap-1.5 min-w-0">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand();
                        }}
                        className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-indigo-300 transition-colors flex items-center justify-center shrink-0 focus:outline-none"
                        title={isExpanded ? "Collapse pipeline" : "Expand pipeline"}
                    >
                        <span className={`text-xs transition-transform duration-200 inline-block ${isExpanded ? 'rotate-90 text-indigo-400' : ''}`}>
                            ▶
                        </span>
                    </button>
                    <span className="material-symbols-outlined text-indigo-400 text-[16px] shrink-0">account_tree</span>
                    {isEditingName ? (
                        <input
                            type="text"
                            autoFocus
                            value={pipelineName}
                            onChange={e => setPipelineName(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onBlur={() => {
                                setIsEditingName(false);
                                if (pipelineName.trim() && pipelineName.trim() !== preset.MappingName) {
                                    onRenamePreset({ ...preset, MappingName: pipelineName.trim() });
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    setIsEditingName(false);
                                    if (pipelineName.trim() && pipelineName.trim() !== preset.MappingName) {
                                        onRenamePreset({ ...preset, MappingName: pipelineName.trim() });
                                    }
                                } else if (e.key === 'Escape') {
                                    setPipelineName(preset.MappingName || `${canonicalEntity} Pipeline`);
                                    setIsEditingName(false);
                                }
                            }}
                            className="bg-slate-950 border border-indigo-500 rounded px-1.5 py-0 h-6 text-xs text-white focus:outline-none w-full"
                        />
                    ) : (
                        <span
                            onDoubleClick={(e) => {
                                e.stopPropagation();
                                setIsEditingName(true);
                            }}
                            className="font-bold text-xs text-white tracking-wide truncate hover:text-indigo-300 transition-colors cursor-pointer select-none"
                            title="Double-click to rename pipeline"
                        >
                            {pipelineName}
                        </span>
                    )}
                </div>

                {/* ── COL 2 (1fr): ERP SOURCE TABLE (SEARCHABLE SELECT) ── */}
                <div onClick={e => e.stopPropagation()} className="min-w-0">
                    <SearchableSelect
                        options={tableOptions}
                        value={selectedTable}
                        onChange={tbl => {
                            const upper = (tbl || '').toUpperCase();
                            setSelectedTable(upper);
                            triggerDebouncedSave();
                        }}
                        placeholder="Select source table..."
                        allowCustom={true}
                        className="w-full text-xs"
                    />
                </div>

                {/* ── COL 3 (80px, center): DIRECTIONAL FLOW ARROW ── */}
                <div className="flex items-center justify-center gap-1 text-center select-none">
                    <span className="text-slate-500 font-bold text-xs">➔</span>
                    <span className="px-1.5 py-0.2 rounded-full bg-slate-900 border border-slate-750 text-indigo-300 font-mono text-[10px]">
                        {mappedFieldsCount} Mapped
                    </span>
                </div>

                {/* ── COL 4 (1fr): TARGET TABLE (FILTERED) ── */}
                <div onClick={e => e.stopPropagation()} className="min-w-0">
                    <SearchableSelect
                        options={entityOptions}
                        value={selectedEntity}
                        onChange={ent => {
                            const canonical = normalizeEntityName(ent);
                            setSelectedEntity(canonical);
                            triggerDebouncedSave();
                        }}
                        placeholder="Select target table..."
                        className="w-full text-xs"
                    />
                </div>

                {/* ── TIER 2 HOVER ACTION HUD (Search fields + All/Mapped/Unmapped + Reset + Delete) ── */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 bg-slate-800/95 border border-slate-700 shadow-xl rounded px-2 py-1 flex items-center gap-2 z-10 transition-opacity">
                    {/* Search fields input */}
                    <div className="relative w-36">
                        <span className="material-symbols-outlined text-slate-500 absolute left-1.5 top-1 text-[13px]">search</span>
                        <input
                            type="text"
                            value={fieldSearch}
                            onChange={e => setFieldSearch(e.target.value)}
                            placeholder="Search fields..."
                            className="w-full bg-slate-950 border border-slate-700 hover:border-slate-600 rounded pl-6 pr-5 py-0 h-6 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                        />
                        {fieldSearch && (
                            <button
                                type="button"
                                onClick={() => setFieldSearch('')}
                                className="absolute right-1.5 top-0.5 text-slate-500 hover:text-white text-xs font-bold"
                            >&times;</button>
                        )}
                    </div>

                    {/* All | Mapped | Unmapped Toggles */}
                    <div className="flex items-center bg-slate-900 border border-slate-700 rounded p-0.5 gap-0.5 text-[10px]">
                        <button
                            type="button"
                            onClick={() => setMappingFilter('all')}
                            className={`px-1.5 py-0.5 rounded font-semibold transition-colors ${
                                mappingFilter === 'all' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            All
                        </button>
                        <button
                            type="button"
                            onClick={() => setMappingFilter('mapped')}
                            className={`px-1.5 py-0.5 rounded font-semibold transition-colors ${
                                mappingFilter === 'mapped' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            Mapped
                        </button>
                        <button
                            type="button"
                            onClick={() => setMappingFilter('unmapped')}
                            className={`px-1.5 py-0.5 rounded font-semibold transition-colors ${
                                mappingFilter === 'unmapped' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            Unmapped
                        </button>
                    </div>

                    {/* Reset Pipeline Defaults Button */}
                    <button
                        type="button"
                        disabled={isDeleting}
                        onClick={() => onResetPresetDefaults(preset)}
                        className="h-6 px-1.5 bg-slate-750 hover:bg-slate-700 text-slate-300 hover:text-white rounded border border-slate-600 text-[10px] font-semibold transition-colors disabled:opacity-50 shrink-0"
                        title="Reset pipeline mappings to defaults"
                    >
                        Reset
                    </button>

                    {/* Delete Pipeline Button */}
                    <button
                        type="button"
                        disabled={isDeleting}
                        onClick={async (e) => {
                            e.stopPropagation();
                            setIsDeleting(true);
                            setDeleteError(false);
                            try {
                                await onDeletePreset(preset);
                            } catch (err) {
                                setDeleteError(true);
                                setIsDeleting(false);
                            }
                        }}
                        className={`h-6 px-1.5 rounded border text-[10px] font-semibold transition-colors flex items-center gap-1 shrink-0 ${
                            isDeleting
                                ? 'bg-slate-800 text-slate-500 border-slate-700 cursor-wait'
                                : 'bg-slate-750 hover:bg-rose-950/60 text-slate-300 hover:text-rose-300 border-slate-600 hover:border-rose-800/60'
                        }`}
                        title="Delete this pipeline"
                    >
                        {isDeleting && (
                            <span className="material-symbols-outlined text-[11px] animate-spin">sync</span>
                        )}
                        <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
                    </button>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* TIER 3: CSS GRID SLIDE ANIMATION CONTAINER (500ms ease-in-out)     */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden min-h-0">
                    {loadingDefinitions ? (
                        <div className="py-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                            <span className="material-symbols-outlined text-indigo-400 animate-spin text-sm">sync</span>
                            Loading field schema definitions...
                        </div>
                    ) : (
                        <div className="p-0">
                            <MappingDesignerTable
                                isStandardMode={true}
                                selectedQBEntity={selectedEntity}
                                activeTableName={selectedTable}
                                mappings={mappings}
                                definitions={definitions}
                                sourceColumns={definitions?.sourceFields || definitions?.sourceColumns || []}
                                onAddMapping={handleAddMappingFromGhost}
                                handleCustomMappingChange={handleCustomMappingChange}
                                handleRemoveCustomRow={handleRemoveCustomRow}
                                handleResetMappingRow={handleResetMappingRow}
                                fieldSearch={fieldSearch}
                                mappingFilter={mappingFilter}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
